import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDashboardPerformance } from '../sales-targets/service';
import moment from 'moment';

/* ─────────────── Yardımcılar ─────────────── */

const fmt = (v: number, isCiro: boolean, currency?: string): string => {
  if (!isCiro) return v.toLocaleString('tr-TR');
  const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₺';
  if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${sym}${(v / 1_000).toFixed(0)}K`;
  return `${sym}${v.toLocaleString('tr-TR')}`;
};

/* Durum renkleri — yeşil / sarı / kırmızı ile görsel olarak iletilir */
const STATUS_COLORS: Record<string, string> = {
  achieved: '#22c55e',
  'on-track': '#eab308',
  'at-risk': '#ef4444',
};
const statusColor = (s: string) => STATUS_COLORS[s] ?? '#94a3b8';

/* ─────────────── Radial Progress ─────────────── */

const RadialProgress: React.FC<{ value: number; accent: string; size?: number }> = ({ value, accent, size = 56 }) => {
  const [anim, setAnim] = useState(0);
  const r = (size - 7) / 2;
  const c = 2 * Math.PI * r;

  useEffect(() => {
    const t = setTimeout(() => setAnim(Math.min(value, 100)), 150);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={4.5} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth={4.5}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (anim / 100) * c}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: size * 0.22,
          color: '#1e293b',
          letterSpacing: '-0.3px',
        }}
      >
        {value.toFixed(0)}%
      </span>
    </div>
  );
};

/* ─────────────── Animated Counter ─────────────── */

const AnimCounter: React.FC<{ target: number; isCiro: boolean; currency?: string }> = ({ target, isCiro, currency }) => {
  const [val, setVal] = useState(0);
  const ref = useRef<number>();

  useEffect(() => {
    const dur = 1200;
    const start = performance.now();
    const animate = (now: number) => {
      const t = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(ease * target));
      if (t < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => {
      if (ref.current) cancelAnimationFrame(ref.current);
    };
  }, [target]);

  return <>{fmt(val, isCiro, currency)}</>;
};

/* ─────────────── Tek Kart — Soft & Minimal ─────────────── */

const TargetCard: React.FC<{
  target: any;
  index: number;
  isCiro: boolean;
}> = ({ target, index, isCiro }) => {
  const sc = statusColor(target.status);
  const rate = Math.min(target.achievement_rate, 100);
  const dateRange = `${moment(target.start_date).format('DD MMM')} – ${moment(target.end_date).format('DD MMM YY')}`;

  // Zaman bazlı gidişat hesaplama
  const now = moment();
  const start = moment(target.start_date);
  const end = moment(target.end_date);
  const totalDays = Math.max(1, end.diff(start, 'days'));
  const elapsedDays = Math.max(0, Math.min(totalDays, now.diff(start, 'days')));
  const remainingDays = Math.max(0, end.diff(now, 'days'));
  const timeProgress = Math.min(100, (elapsedDays / totalDays) * 100);
  const isActive = now.isBetween(start, end, 'day', '[]');
  const isCompleted = now.isAfter(end);

  // Dönem sonu tahmini — mevcut hıza göre dönem sonunda ulaşılacak tahmini %
  const forecastRate = isActive && timeProgress > 0 ? Math.round((target.achievement_rate / timeProgress) * 100) : isCompleted ? Math.round(target.achievement_rate) : 0;

  // Kalan gün etiketi
  const remainLabel = isCompleted ? 'Süre doldu' : isActive ? `${remainingDays} gün kaldı` : `${Math.abs(now.diff(start, 'days'))} gün sonra başlıyor`;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        padding: '20px 22px 18px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        border: '1px solid #f1f5f9',
        borderLeft: `3px solid ${sc}`,
        boxShadow: '0 1px 3px rgba(0,0,0,.04)',
        transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,.07)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.04)';
      }}
    >
      {/* Üst: Başlık + Radial Progress */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
          <h6
            style={{
              margin: 0,
              fontSize: '.85rem',
              fontWeight: 600,
              color: '#1e293b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={target.target_name}
          >
            {target.target_name}
          </h6>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '.7rem', color: '#94a3b8', fontWeight: 500 }}>{dateRange}</span>
            {target.brand && (
              <span
                style={{
                  padding: '1px 7px',
                  borderRadius: 4,
                  background: '#f1f5f9',
                  color: '#64748b',
                  fontSize: '.62rem',
                  fontWeight: 600,
                }}
              >
                {target.brand}
              </span>
            )}
          </div>
        </div>
        <RadialProgress value={rate} accent={sc} />
      </div>

      {/* Orta: Büyük rakam */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.3px', lineHeight: 1.15 }}>
          <AnimCounter target={target.actual_amount} isCiro={isCiro} currency={target.currency} />
        </span>
        <span style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: 500 }}>
          / {fmt(target.target_amount, isCiro, target.currency)} {isCiro ? '' : 'adet'} hedef
        </span>
      </div>

      {/* Progress bar — beklenen ilerleme marker'ı ile */}
      <div style={{ position: 'relative', marginBottom: 6 }}>
        <div style={{ height: 5, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
          <div
            style={{
              width: `${rate}%`,
              height: '100%',
              borderRadius: 3,
              background: sc,
              transition: 'width 1s cubic-bezier(.4,0,.2,1)',
            }}
          />
        </div>
        {/* Beklenen ilerleme çizgisi — sadece aktif dönemde göster */}
        {isActive && timeProgress > 2 && timeProgress < 98 && (
          <div
            style={{
              position: 'absolute',
              top: -1,
              left: `${timeProgress}%`,
              width: 1.5,
              height: 7,
              background: '#94a3b8',
              borderRadius: 1,
            }}
            title={`Beklenen: %${timeProgress.toFixed(0)}`}
          />
        )}
      </div>

      {/* Zaman bilgisi satırı */}
      <div style={{ marginBottom: 10 }}>
        {isActive && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '.64rem', color: '#b0b8c4', fontWeight: 500 }}>Geçen süre</span>
              <span style={{ fontSize: '.64rem', color: '#64748b', fontWeight: 600 }}>
                {elapsedDays}/{totalDays} gün
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '.64rem', color: '#b0b8c4', fontWeight: 500 }}>Beklenen</span>
              <span style={{ fontSize: '.64rem', color: '#64748b', fontWeight: 600 }}>%{timeProgress.toFixed(0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '.64rem', color: '#b0b8c4', fontWeight: 500 }}>Dönem sonu tahmini</span>
              <span style={{ fontSize: '.64rem', fontWeight: 600, color: forecastRate >= 100 ? '#22c55e' : forecastRate >= 80 ? '#eab308' : '#ef4444' }}>%{forecastRate}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '.64rem', color: '#b0b8c4', fontWeight: 500 }}>Kalan</span>
              <span style={{ fontSize: '.64rem', fontWeight: 600, color: remainingDays <= 7 ? '#d97706' : '#64748b' }}>{remainLabel}</span>
            </div>
          </div>
        )}
        {isCompleted && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '.64rem', color: '#b0b8c4', fontWeight: 500 }}>{totalDays} gün tamamlandı</span>
            <span style={{ fontSize: '.64rem', fontWeight: 600, color: '#dc2626' }}>{remainLabel}</span>
          </div>
        )}
        {!isActive && !isCompleted && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '.64rem', color: '#b0b8c4', fontWeight: 500 }}>Toplam {totalDays} gün</span>
            <span style={{ fontSize: '.64rem', fontWeight: 600, color: '#64748b' }}>{remainLabel}</span>
          </div>
        )}
      </div>

      {/* Alt: Ürün grubu */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ fontSize: '.65rem', color: '#b0b8c4', fontWeight: 500 }}>{target.product_group}</span>
      </div>
    </div>
  );
};

/* ─────────────── Ana Widget ─────────────── */

export const DashboardTargetsWidget: React.FC = () => {
  const { data, isLoading, error } = useDashboardPerformance((err: any) => {
    console.error('Dashboard verisi yüklenirken hata:', err);
  });

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState<'adet' | 'ciro'>('adet');

  /* ── Filtreli hedefler ── */
  const adetTargets = useMemo(() => data?.performance?.filter((t: any) => !t.target_type || t.target_type === 'adet') || [], [data?.performance]);
  const ciroTargets = useMemo(() => data?.performance?.filter((t: any) => t.target_type === 'ciro') || [], [data?.performance]);
  const targets = activeTab === 'adet' ? adetTargets : ciroTargets;
  const total = targets.length;

  /* ── Slider state ── */
  const VISIBLE = 3;
  const [startIdx, setStartIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'exit' | 'enter'>('idle');
  const [direction, setDirection] = useState<'left' | 'right'>('left');
  const needsSlider = total > VISIBLE;

  /* ── Mobil state ── */
  const [isMobile, setIsMobile] = useState(false);
  const [mobileIdx, setMobileIdx] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 992);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Tab değişti → slider sıfırla ── */
  useEffect(() => {
    setStartIdx(0);
    setMobileIdx(0);
    setPhase('idle');
  }, [activeTab]);

  /* ── Desktop auto-slide ── */
  const slideNext = useCallback(() => {
    if (phase !== 'idle' || total <= VISIBLE) return;
    setDirection('left');
    setPhase('exit');
    setTimeout(() => {
      setStartIdx((p) => (p + 1) % total);
      setPhase('enter');
      setTimeout(() => setPhase('idle'), 350);
    }, 250);
  }, [phase, total]);

  const slidePrev = useCallback(() => {
    if (phase !== 'idle' || total <= VISIBLE) return;
    setDirection('right');
    setPhase('exit');
    setTimeout(() => {
      setStartIdx((p) => (p - 1 + total) % total);
      setPhase('enter');
      setTimeout(() => setPhase('idle'), 350);
    }, 250);
  }, [phase, total]);

  const slideTo = useCallback(
    (idx: number) => {
      if (phase !== 'idle') return;
      setDirection(idx > startIdx ? 'left' : 'right');
      setPhase('exit');
      setTimeout(() => {
        setStartIdx(idx);
        setPhase('enter');
        setTimeout(() => setPhase('idle'), 350);
      }, 250);
    },
    [phase, startIdx],
  );

  useEffect(() => {
    if (!needsSlider || paused || isMobile) return;
    const iv = setInterval(slideNext, 4000);
    return () => clearInterval(iv);
  }, [needsSlider, paused, isMobile, slideNext]);

  /* ── Mobil auto-slide ── */
  useEffect(() => {
    if (!isMobile || total <= 1 || paused) return;
    const iv = setInterval(() => {
      setIsAutoScrolling(true);
      setMobileIdx((p) => (p + 1) % total);
      setTimeout(() => setIsAutoScrolling(false), 600);
    }, 4000);
    return () => clearInterval(iv);
  }, [isMobile, total, paused]);

  useEffect(() => {
    if (!sliderRef.current || !isMobile) return;
    const w = sliderRef.current.scrollWidth / total;
    sliderRef.current.scrollTo({ left: w * mobileIdx, behavior: 'smooth' });
  }, [mobileIdx, isMobile, total]);

  const onMobileScroll = useCallback(() => {
    if (!sliderRef.current || !isMobile || isAutoScrolling) return;
    const w = sliderRef.current.scrollWidth / total;
    const idx = Math.round(sliderRef.current.scrollLeft / w);
    if (idx !== mobileIdx) setMobileIdx(idx);
  }, [mobileIdx, isMobile, isAutoScrolling, total]);

  /* ── Visible kartlar (desktop, döngüsel) ── */
  const visibleCards: { target: any; idx: number }[] = [];
  if (total > 0) {
    const count = Math.min(VISIBLE, total);
    for (let i = 0; i < count; i++) {
      const realIdx = (startIdx + i) % total;
      visibleCards.push({ target: targets[realIdx], idx: realIdx });
    }
  }

  /* ── Summary stats ── */
  const summaryStats = useMemo(() => {
    const achieved = targets.filter((t: any) => t.status === 'achieved').length;
    const onTrack = targets.filter((t: any) => t.status === 'on-track').length;
    const atRisk = targets.filter((t: any) => t.status === 'at-risk').length;
    return { achieved, onTrack, atRisk };
  }, [targets]);

  const lastSync = data?.summary?.last_sync ?? null;

  /* ─────────────── Loading skeleton ─────────────── */
  if (isLoading) {
    return (
      <div className="col-12 mb-6">
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            padding: '24px 28px',
            border: '1px solid #f1f5f9',
          }}
        >
          <div className="placeholder-glow d-flex gap-3 mb-4">
            <span className="placeholder rounded-2" style={{ width: 110, height: 34 }} />
            <span className="placeholder rounded-2" style={{ width: 110, height: 34 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[1, 2, 3].map((k) => (
              <div
                key={k}
                className="placeholder-glow"
                style={{
                  borderRadius: 14,
                  height: 190,
                  background: '#f8fafc',
                  border: '1px solid #f1f5f9',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="col-12 mb-5">
        <div className="alert alert-danger mb-0" style={{ borderRadius: 16 }}>
          Satış hedefleri yüklenirken hata oluştu
        </div>
      </div>
    );
  }

  if (adetTargets.length === 0 && ciroTargets.length === 0) return null;

  /* ─────────────── Render ─────────────── */
  const dotIdx = startIdx % total;

  return (
    <div className="col-12 mb-6">
      <style>{`
        .targets-widget-tab {
          padding: 7px 18px;
          border: 1px solid transparent;
          border-radius: 8px;
          font-weight: 600;
          font-size: .8rem;
          cursor: pointer;
          transition: all .2s ease;
          letter-spacing: .1px;
          background: none;
        }
        .targets-widget-tab.active {
          background: #f8fafc;
          color: #1e293b;
          border-color: #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,.04);
        }
        .targets-widget-tab:not(.active) {
          background: transparent;
          color: #94a3b8;
        }
        .targets-widget-tab:not(.active):hover {
          color: #64748b;
          background: #f8fafc;
        }
        @keyframes tw-exit-left {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-30px); opacity: 0; }
        }
        @keyframes tw-enter-right {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes tw-exit-right {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(30px); opacity: 0; }
        }
        @keyframes tw-enter-left {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .tw-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          border: none;
          padding: 0;
          transition: all .3s ease;
          cursor: pointer;
        }
        .tw-dot.active {
          width: 20px;
          border-radius: 3px;
          background: #64748b;
        }
        .tw-dot:not(.active) {
          background: #e2e8f0;
        }
        .tw-dot:not(.active):hover {
          background: #cbd5e1;
        }
        .tw-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: .75rem;
          box-shadow: 0 2px 8px rgba(0,0,0,.06);
          transition: all .2s ease;
          opacity: 0;
          z-index: 2;
        }
        .tw-slider-wrap:hover .tw-arrow {
          opacity: 1;
        }
        .tw-arrow:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #1e293b;
          box-shadow: 0 3px 12px rgba(0,0,0,.1);
        }
        .tw-arrow:active {
          transform: translateY(-50%) scale(.92);
        }
        .tw-arrow-left { left: -16px; }
        .tw-arrow-right { right: -16px; }
        .tw-mobile-slider {
          display: flex;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 8px;
        }
        .tw-mobile-slider::-webkit-scrollbar { display: none; }
        .tw-mobile-card {
          flex: 0 0 85%;
          scroll-snap-align: center;
          padding: 0 6px;
        }
        @media (min-width: 576px) and (max-width: 991px) {
          .tw-mobile-card { flex: 0 0 48%; }
        }
      `}</style>

      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid #f1f5f9',
          boxShadow: '0 1px 2px rgba(0,0,0,.03)',
        }}
      >
        {/* ── Header ── */}
        <div style={{ padding: '24px 28px 0' }}>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            {/* Tabs */}
            <div className="d-flex align-items-center gap-2">
              <button className={`targets-widget-tab ${activeTab === 'adet' ? 'active' : ''}`} onClick={() => setActiveTab('adet')}>
                Adet Hedefleri
                {adetTargets.length > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: '.66rem',
                      background: activeTab === 'adet' ? '#e2e8f0' : '#f1f5f9',
                      color: activeTab === 'adet' ? '#475569' : '#94a3b8',
                      fontWeight: 700,
                    }}
                  >
                    {adetTargets.length}
                  </span>
                )}
              </button>
              <button className={`targets-widget-tab ${activeTab === 'ciro' ? 'active' : ''}`} onClick={() => setActiveTab('ciro')}>
                Ciro Hedefleri
                {ciroTargets.length > 0 && (
                  <span
                    style={{
                      marginLeft: 6,
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: '.66rem',
                      background: activeTab === 'ciro' ? '#e2e8f0' : '#f1f5f9',
                      color: activeTab === 'ciro' ? '#475569' : '#94a3b8',
                      fontWeight: 700,
                    }}
                  >
                    {ciroTargets.length}
                  </span>
                )}
              </button>
            </div>

            {/* Özet göstergeler */}
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                <span style={{ fontSize: '.7rem', color: '#64748b', fontWeight: 500 }}>{summaryStats.achieved}</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#eab308', display: 'inline-block' }} />
                <span style={{ fontSize: '.7rem', color: '#64748b', fontWeight: 500 }}>{summaryStats.onTrack}</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                <span style={{ fontSize: '.7rem', color: '#64748b', fontWeight: 500 }}>{summaryStats.atRisk}</span>
              </div>
              {lastSync && (
                <span style={{ fontSize: '.68rem', color: '#94a3b8', marginLeft: 4 }}>
                  <i className="fas fa-sync-alt me-1" style={{ fontSize: '.6rem' }} />
                  {moment(lastSync as string).format('DD.MM.YYYY HH:mm')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── İçerik ── */}
        <div style={{ padding: '0 28px 24px' }}>
          {total === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '48px 0',
                color: '#94a3b8',
                fontSize: '.9rem',
              }}
            >
              <div style={{ fontSize: '1.5rem', marginBottom: 8, color: '#cbd5e1' }}>—</div>
              <span style={{ color: '#94a3b8' }}>Aktif {activeTab === 'adet' ? 'adet' : 'ciro'} hedefi bulunmuyor</span>
            </div>
          ) : (
            <>
              {/* ── Desktop 3-kolon grid + slider ── */}
              <div className="d-none d-lg-block" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                <div className="tw-slider-wrap" style={{ position: 'relative' }}>
                  {/* Sol ok */}
                  {needsSlider && (
                    <button
                      className="tw-arrow tw-arrow-left"
                      onClick={() => {
                        slidePrev();
                        setPaused(true);
                        setTimeout(() => setPaused(false), 10000);
                      }}
                    >
                      <i className="fas fa-chevron-left" />
                    </button>
                  )}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.min(VISIBLE, total)}, 1fr)`,
                      gap: 16,
                      animation:
                        phase === 'exit'
                          ? direction === 'left'
                            ? 'tw-exit-left .25s ease-in forwards'
                            : 'tw-exit-right .25s ease-in forwards'
                          : phase === 'enter'
                          ? direction === 'left'
                            ? 'tw-enter-right .35s ease-out forwards'
                            : 'tw-enter-left .35s ease-out forwards'
                          : 'none',
                    }}
                  >
                    {visibleCards.map(({ target, idx }) => (
                      <TargetCard key={`${target.id}-${startIdx}-${idx}`} target={target} index={idx} isCiro={activeTab === 'ciro'} />
                    ))}
                  </div>

                  {/* Sağ ok */}
                  {needsSlider && (
                    <button
                      className="tw-arrow tw-arrow-right"
                      onClick={() => {
                        slideNext();
                        setPaused(true);
                        setTimeout(() => setPaused(false), 10000);
                      }}
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  )}
                </div>

                {/* Dot göstergeleri */}
                {needsSlider && (
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                    {targets.map((_: any, i: number) => (
                      <button
                        key={i}
                        className={`tw-dot ${dotIdx === i ? 'active' : ''}`}
                        onClick={() => {
                          slideTo(i);
                          setPaused(true);
                          setTimeout(() => setPaused(false), 10000);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Mobil horizontal slider ── */}
              <div className="d-lg-none" onTouchStart={() => setPaused(true)} onTouchEnd={() => setTimeout(() => setPaused(false), 8000)}>
                <div ref={sliderRef} className="tw-mobile-slider" onScroll={onMobileScroll}>
                  {targets.map((target: any, i: number) => (
                    <div key={target.id} className="tw-mobile-card">
                      <TargetCard target={target} index={i} isCiro={activeTab === 'ciro'} />
                    </div>
                  ))}
                </div>

                {total > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                    {targets.map((_: any, i: number) => (
                      <button key={i} className={`tw-dot ${mobileIdx === i ? 'active' : ''}`} onClick={() => setMobileIdx(i)} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTargetsWidget;
